import React, { useEffect, useRef, useState } from 'react';
import { 
  Eye, BookOpen, Users, Lightbulb, TrendingUp, Award, ChevronRight, 
  Star, Target, Sparkles, Quote, GraduationCap, Zap, Globe, 
  Heart, Clock, Shield, ArrowRight, Play, CircleDot
} from 'lucide-react';
const VisionAboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const features = [
    { icon: Award, title: "Renowned faculty", color: "sky" },
    { icon: TrendingUp, title: "Unlimited learning opportunities", color: "sky" },
    { icon: Users, title: "Vibrant student life", color: "sky" },
    { icon: Lightbulb, title: "New Ideas to generate and new shape to the world", color: "sky" }
  ];
  return (
    <section ref={sectionRef} className="relative py-16 md:py-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-sky-50/30">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-72 h-72 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-red-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <div className={`text-center mb-12 md:mb-16 transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-100 via-red-100 to-sky-100 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-sky-500" />
            <span className="text-xs font-semibold bg-gradient-to-r from-sky-600 to-red-600 bg-clip-text text-transparent">
              Our Commitment to Excellence
            </span>
            <Sparkles className="w-4 h-4 text-red-500" />
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Shaping Tomorrow's
            </span>
            <br />
            <span className="bg-gradient-to-r from-sky-600 via-red-500 to-sky-600 bg-clip-text text-transparent animate-gradient">
              Leaders Today
            </span>
          </h2>
          <div className="w-20 h-0.5 bg-gradient-to-r from-sky-500 via-red-500 to-sky-500 mx-auto rounded-full"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className={`transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
            <div className="group relative h-full">
              <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 h-full">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 via-sky-500 to-sky-600"></div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gradient-to-br from-sky-500 to-sky-600 p-2.5 rounded-xl shadow-lg">
                      <Eye className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-800">Our Vision</h2>
                      <div className="w-12 h-0.5 bg-gradient-to-r from-sky-500 to-sky-600 mt-1 rounded-full"></div>
                    </div>
                  </div>
                  <div className="relative mb-5 p-4 bg-gradient-to-r from-sky-50 to-indigo-50 rounded-xl border-l-4 border-sky-500">
                    <Quote className="absolute top-2 right-2 w-5 h-5 text-sky-300 opacity-50" />
                    <p className="text-gray-700 italic text-sm md:text-base leading-relaxed">
                      "Like our former president Dr. A.P.J. Abdul Kalam, it is our wishful thinking to visualize 100% literacy in the nation by 2020"
                    </p>
                  </div>
                  <div className="space-y-4 mb-6">
                    <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                      By fulfilling our sacred duty of providing a platform which is very essential and aims at multi-dimensional 
                      objectives to take youngsters to glorious heights in different fields...
                    </p>
                    <p className="text-gray-600 leading-relaxed text-sm md:text-base pl-3 border-l-2 border-sky-400">
                      ...and transforming them into tomorrow's entrepreneurs who will be proficient in their respective fields.
                    </p>
                  </div>
                  <button className="inline-flex items-center gap-2 text-sky-600 font-semibold hover:text-sky-700 transition-all duration-300 text-sm md:text-base">
                    <span>Read More</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className={`transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
            <div className="group relative h-full">
              <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 h-full">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 via-red-500 to-red-600"></div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gradient-to-br from-red-500 to-red-600 p-2.5 rounded-xl shadow-lg">
                      <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-800">About RDS College</h2>
                      <div className="w-12 h-0.5 bg-gradient-to-r from-red-500 to-red-600 mt-1 rounded-full"></div>
                    </div>
                  </div>
                  <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-l-4 border-red-500">
                    <p className="text-gray-800 font-semibold text-sm md:text-base">
                      Where pioneering spirit meets 21st century technology & which is known for:
                    </p>
                  </div>
                  <div className="space-y-3 mb-6">
                    {features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-all duration-300">
                        <div className="bg-sky-100 p-1.5 rounded-lg mt-0.5">
                          <feature.icon className="w-3.5 h-3.5 text-sky-600" />
                        </div>
                        <span className="text-gray-700 text-sm md:text-base">{feature.title}</span>
                      </div>
                    ))}
                  </div>
                  <button className="inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition-all duration-300 text-sm md:text-base">
                    <span>Read More</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default VisionAboutSection;